import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-movie-2-fill text-primary-500 text-2xl"></i>
            </div>
            <span className="font-heading text-xl font-bold text-foreground-50">
              星空影院
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : 'text-foreground-300 hover:text-foreground-50'
              }`}
            >
              首页
            </Link>
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer text-foreground-300 hover:text-foreground-50 transition-colors">
                分类
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-background-100 border border-background-200/60 rounded-lg p-3 grid grid-cols-4 gap-1 min-w-[480px]">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-300 hover:text-foreground-50 hover:bg-background-200/50 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link
              to="/category/all"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                location.pathname.startsWith('/category')
                  ? 'text-primary-500'
                  : 'text-foreground-300 hover:text-foreground-50'
              }`}
            >
              排行榜
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                location.pathname === '/about'
                  ? 'text-primary-500'
                  : 'text-foreground-300 hover:text-foreground-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                placeholder="搜索电影..."
                className="w-48 pl-9 pr-3 py-2 rounded-full bg-background-200/50 border border-background-300/50 text-sm text-foreground-100 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
            </div>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer text-foreground-100"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50/95 backdrop-blur-md border-t border-background-200/60">
          <div className="px-4 py-4 flex flex-col gap-2">
            <Link to="/" className="block px-3 py-2 rounded-md text-sm text-foreground-100 hover:bg-background-200/50 cursor-pointer whitespace-nowrap">首页</Link>
            <p className="px-3 py-1 text-xs text-foreground-500 font-medium">分类浏览</p>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-300 hover:text-foreground-50 hover:bg-background-200/50 cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="block px-3 py-2 rounded-md text-sm text-foreground-100 hover:bg-background-200/50 cursor-pointer whitespace-nowrap">关于我们</Link>
            <div className="relative mt-2">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                placeholder="搜索电影..."
                className="w-full pl-9 pr-3 py-2 rounded-full bg-background-200/50 border border-background-300/50 text-sm text-foreground-100 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}